import type {
  DeclineDataPoint,
  DeclineFitResult,
} from './declineCalculations';

import {
  predictDeclineRate,
} from './declineCalculations';

export type DeclineIssueType =
  | '长时间间隔'
  | '产量突增'
  | '孤立异常点'
  | '重复时间点';

export interface DeclineDataIssue {
  index: number;
  date: string;
  type: DeclineIssueType;
  message: string;

  /**
   * true:
   * 建议在自动预处理中排除
   */
  excludeSuggested: boolean;
}

export interface SegmentedForecastPoint {
  t: number;
  q: number;
  phase:
    | 'hyperbolic'
    | 'exponential';
}

export interface SegmentedDeclineResult {
  terminalAnnualEffectiveDecline: number;

  /**
   * 终端指数段名义递减率
   * 单位 1/d
   */
  terminalNominalDecline: number;

  /**
   * 从拟合起点开始计算
   */
  transitionT: number;

  transitionQ: number;

  forecastEndT: number;

  finalQ: number;

  cumulativeHyperbolic: number;

  cumulativeExponential: number;

  cumulativeTotal: number;

  curve: SegmentedForecastPoint[];
}

function median(
  values: number[],
) {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [
    ...values,
  ].sort(
    (a, b) => a - b,
  );

  const middle =
    Math.floor(
      sorted.length / 2,
    );

  if (
    sorted.length % 2 === 0
  ) {
    return (
      sorted[middle - 1] +
      sorted[middle]
    ) / 2;
  }

  return sorted[middle];
}

/**
 * 对生产数据做“候选异常”识别。
 *
 * 注意：
 * 这里只做提示，不直接把“产量上涨”
 * 等价成异常，因为现场中可能确实发生
 * 节流制度调整、复产、措施等。
 */
export function detectDeclineDataIssues(
  data: DeclineDataPoint[],
): DeclineDataIssue[] {
  if (data.length < 2) {
    return [];
  }

  const issues:
    DeclineDataIssue[] = [];

  const intervals =
    data
      .slice(1)
      .map(
        (
          point,
          index,
        ) =>
          point.t -
          data[index].t,
      )
      .filter(
        (value) =>
          value > 0 &&
          Number.isFinite(
            value,
          ),
      );

  const medianInterval =
    median(intervals);

  const longGapThreshold =
    Math.max(
      45,
      medianInterval > 0
        ? medianInterval * 2.5
        : 45,
    );

  for (
    let i = 1;
    i < data.length;
    i += 1
  ) {
    const previous =
      data[i - 1];

    const current =
      data[i];

    const dt =
      current.t -
      previous.t;

    if (dt <= 0) {
      issues.push({
        index: i,
        date:
          current.date,
        type:
          '重复时间点',
        message:
          '该记录与前一条记录时间相同或时间顺序异常。',
        excludeSuggested:
          true,
      });

      continue;
    }

    if (
      dt >
      longGapThreshold
    ) {
      issues.push({
        index: i,
        date:
          current.date,
        type:
          '长时间间隔',
        message:
          `与前一生产点相隔 ${dt.toFixed(
            0,
          )} d，可能存在关井、停产或资料缺失。`,
        excludeSuggested:
          false,
      });
    }

    if (
      previous.q > 0 &&
      current.q >
        previous.q * 1.25
    ) {
      issues.push({
        index: i,
        date:
          current.date,
        type:
          '产量突增',
        message:
          `日产气量较上一点增加 ${(
            (
              current.q /
                previous.q -
              1
            ) *
            100
          ).toFixed(
            1,
          )}%，建议核查复产、措施或生产制度变化。`,
        excludeSuggested:
          false,
      });
    }
  }

  /*
   * 孤立异常点识别：
   * 当前点明显偏离左右两个相邻点，
   * 但左右点本身变化较连续。
   */
  for (
    let i = 1;
    i <
    data.length - 1;
    i += 1
  ) {
    const previous =
      data[i - 1].q;

    const current =
      data[i].q;

    const next =
      data[i + 1].q;

    if (
      previous <= 0 ||
      current <= 0 ||
      next <= 0
    ) {
      continue;
    }

    const neighborRatio =
      Math.max(
        previous,
        next,
      ) /
      Math.min(
        previous,
        next,
      );

    const expected =
      Math.sqrt(
        previous * next,
      );

    const ratio =
      current / expected;

    if (
      neighborRatio < 1.35 &&
      (
        ratio > 1.5 ||
        ratio < 0.67
      )
    ) {
      issues.push({
        index: i,
        date:
          data[i].date,
        type:
          '孤立异常点',
        message:
          `该点相对相邻趋势偏离约 ${(
            Math.abs(
              ratio - 1,
            ) * 100
          ).toFixed(
            1,
          )}%，建议检查计量或生产状态。`,
        excludeSuggested:
          true,
      });
    }
  }

  return issues;
}

function integrateRate(
  fn: (
    t: number,
  ) => number,
  startT: number,
  endT: number,
  stepDays = 1,
) {
  if (
    endT <= startT
  ) {
    return 0;
  }

  let cumulative = 0;

  let t0 =
    startT;

  let q0 =
    fn(t0);

  while (
    t0 < endT
  ) {
    const t1 =
      Math.min(
        t0 +
          stepDays,
        endT,
      );

    const q1 =
      fn(t1);

    if (
      Number.isFinite(q0) &&
      Number.isFinite(q1)
    ) {
      cumulative +=
        0.5 *
        (
          q0 + q1
        ) *
        (
          t1 - t0
        );
    }

    t0 = t1;
    q0 = q1;
  }

  return cumulative;
}

/**
 * Modified Hyperbolic Decline:
 *
 * 前期：
 * q = qi /
 * (1 + b Di t)^(1/b)
 *
 * 当瞬时名义递减率下降到终端递减率后，
 * 转为指数递减。
 */
export function buildSegmentedDecline(
  hyperbolic:
    DeclineFitResult,
  terminalAnnualEffectivePct:
    number,
  forecastEndT: number,
): SegmentedDeclineResult | null {
  if (
    hyperbolic.model !==
      '双曲递减' ||
    hyperbolic.qi <= 0 ||
    hyperbolic.di < 0 ||
    hyperbolic.b <= 0
  ) {
    return null;
  }

  const annualEffective =
    Math.min(
      0.95,
      Math.max(
        0.001,
        terminalAnnualEffectivePct /
          100,
      ),
    );

  /*
   * 有效年递减率转换为
   * 连续指数形式的日名义递减率
   */
  const terminalNominal =
    -Math.log(
      1 -
        annualEffective,
    ) / 365;

  let transitionT = 0;

  if (
    hyperbolic.di >
    terminalNominal
  ) {
    transitionT =
      (
        hyperbolic.di /
          terminalNominal -
        1
      ) /
      (
        hyperbolic.b *
        hyperbolic.di
      );
  }

  transitionT =
    Math.max(
      0,
      transitionT,
    );

  const transitionQ =
    predictDeclineRate(
      hyperbolic,
      transitionT,
    );

  const predictSegmented = (
    t: number,
  ) => {
    if (
      t <= transitionT
    ) {
      return predictDeclineRate(
        hyperbolic,
        t,
      );
    }

    return (
      transitionQ *
      Math.exp(
        -terminalNominal *
          (
            t -
            transitionT
          ),
      )
    );
  };

  const endT =
    Math.max(
      0,
      forecastEndT,
    );

  const hyperbolicEnd =
    Math.min(
      endT,
      transitionT,
    );

  const cumulativeHyperbolic =
    integrateRate(
      predictSegmented,
      0,
      hyperbolicEnd,
    );

  const cumulativeExponential =
    endT >
    transitionT
      ? integrateRate(
          predictSegmented,
          transitionT,
          endT,
        )
      : 0;

  const curve:
    SegmentedForecastPoint[] =
    [];

  const step =
    Math.max(
      5,
      endT / 180,
    );

  for (
    let t = 0;
    t <= endT;
    t += step
  ) {
    curve.push({
      t,
      q:
        predictSegmented(
          t,
        ),
      phase:
        t <=
        transitionT
          ? 'hyperbolic'
          : 'exponential',
    });
  }

  if (
    curve.length === 0 ||
    curve[
      curve.length - 1
    ].t < endT
  ) {
    curve.push({
      t: endT,
      q:
        predictSegmented(
          endT,
        ),
      phase:
        endT <=
        transitionT
          ? 'hyperbolic'
          : 'exponential',
    });
  }

  return {
    terminalAnnualEffectiveDecline:
      annualEffective,

    terminalNominalDecline:
      terminalNominal,

    transitionT,

    transitionQ,

    forecastEndT:
      endT,

    finalQ:
      predictSegmented(
        endT,
      ),

    cumulativeHyperbolic,

    cumulativeExponential,

    cumulativeTotal:
      cumulativeHyperbolic +
      cumulativeExponential,

    curve,
  };
}